import Button from '@atoms/Button/Button';
import Icon from '@atoms/Icon/Icon';
import { IStoreLocatorDealer } from '@models/IStoreLocatorDealer';
import updateDataLayer from '@utils/helpers/gtm';
import classNames from 'classnames';

interface Props {
  readonly dealer: IStoreLocatorDealer;
  readonly directionsLabel: string;
  readonly dataTestId: string;
  readonly goToDealerLabel: string;
  readonly isSelectedDealer?: boolean;
  readonly viewOnMapLabel: string;
  readonly onViewOnMapClick?: (currentDealer: IStoreLocatorDealer) => void;
}

const DealersListingCard = ({
  dealer,
  directionsLabel,
  dataTestId,
  goToDealerLabel,
  isSelectedDealer,
  viewOnMapLabel,
  onViewOnMapClick,
}: Props) => {
  const {
    title,
    addressLine1,
    addressLine2,
    city,
    postCode,
    distance,
    distanceMeasurement,
    latitude,
    longitude,
    dealerUrl,
  } = dealer;

  return (
    <li
      data-testid={dataTestId}
      className={classNames(
        'w-full cursor-default overflow-hidden rounded-[10px] bg-white px-6 py-7.5 transition-shadow duration-300 lg:px-7.5',
        isSelectedDealer
          ? 'shadow-[0px_0px_10px_0px_rgba(0,0,0,0.3)]'
          : 'hover:shadow-[0px_0px_10px_0px_rgba(0,0,0,0.1)]'
      )}
    >
      <p
        className="body-one body-one--bold mb-2 text-black-100 md:mb-2.5"
        data-testid={`${dataTestId}-Title`}
      >
        {title}
      </p>

      <div
        className="mb-2 flex items-center md:mb-2.5"
        data-testid={`${dataTestId}-Distance`}
      >
        <div className="mb-[3px] mr-1.5 flex" aria-label="Map pin icon">
          <Icon
            name="map_pin"
            size="10px"
            colorClassName="text-grey-450 md:text-black-100"
          />
        </div>

        <div className="body-four text-grey-600">
          {parseFloat(distance).toFixed(1)}
          {distanceMeasurement}
        </div>
      </div>

      <address className="body-three mb-5 flex flex-col not-italic text-grey-450 md:hidden">
        {addressLine1 && (
          <span data-testid={`${dataTestId}-Mobile-Address-Line-1`}>
            {addressLine1}
          </span>
        )}
        {addressLine2 && (
          <span data-testid={`${dataTestId}-Mobile-Address-Line-2`}>
            {addressLine2}
          </span>
        )}
        {city && <span data-testid={`${dataTestId}-Mobile-City`}>{city}</span>}
        {postCode && (
          <span data-testid={`${dataTestId}-Mobile-Post-Code`}>{postCode}</span>
        )}
      </address>

      <address className="body-three mb-7.5 not-italic text-grey-600 max-md:hidden">
        {addressLine1 && (
          <span data-testid={`${dataTestId}-Desktop-Address-Line-1`}>
            {addressLine1}
          </span>
        )}
        {addressLine2 && (
          <span data-testid={`${dataTestId}-Desktop-Address-Line-2`}>
            ,&nbsp;{addressLine2}
          </span>
        )}
        {city && (
          <span data-testid={`${dataTestId}-Desktop-City`}>,&nbsp;{city}</span>
        )}
        {postCode && (
          <span data-testid={`${dataTestId}-Desktop-Post-Code`}>
            ,&nbsp;{postCode}
          </span>
        )}
      </address>

      <div
        className={classNames('lg:flex lg:flex-wrap lg:gap-x-5 lg:gap-y-2.5', {
          'lg:justify-between': !!dealerUrl,
        })}
      >
        <div className="flex flex-col gap-x-5 gap-y-2.5 md:flex-row md:flex-wrap">
          {dealerUrl && (
            <div>
              <Button
                dataTestId={`${dataTestId}-Go-To-Dealer-Button`}
                link={{
                  name: goToDealerLabel,
                  url: dealerUrl,
                  target: '_blank',
                }}
                linkCallback={() => {
                  updateDataLayer({
                    event: 'storeFinder',
                    content_type: 'store_listing',
                    store_name: dealer.title,
                    link_text: goToDealerLabel,
                  });
                }}
              />
            </div>
          )}

          <div>
            <Button
              dataTestId={`${dataTestId}-Directions-Button`}
              secondary
              link={{
                name: directionsLabel,
                url: `https://www.google.com/maps/dir/?api=1&destination=${encodeURI(
                  `${latitude},${longitude}`
                )}`,
                target: '_blank',
              }}
              linkCallback={() => {
                updateDataLayer({
                  event: 'storeFinder',
                  content_type: 'store_listing',
                  store_name: dealer.title,
                  link_text: directionsLabel,
                });
              }}
            />
          </div>
        </div>

        <div className="max-lg:hidden">
          <Button
            dataTestId={`${dataTestId}-View-On-Map-Button`}
            inverted
            onClick={() => {
              onViewOnMapClick?.(dealer);
              updateDataLayer({
                event: 'storeFinder',
                content_type: 'store_listing',
                store_name: dealer.title,
                link_text: viewOnMapLabel,
              });
            }}
          >
            {viewOnMapLabel}
          </Button>
        </div>
      </div>
    </li>
  );
};

export default DealersListingCard;
